import React from 'react';
import { useParams } from 'react-router-dom';
import { ArcPolyline } from '@src/core/controllers';
import { InfoWindow, Marker } from '@react-google-maps/api';
import { ArcButton, ArcCellInfo, ArcStack, ArcText } from '@src/core/ui';
import { useConfirm } from '@src/core/controllers/confirm-controller/hooks';
import gohere from '@src/assets/gohere.svg';
import takeoff from '@src/assets/takeoff.svg';
import destionation from '@src/assets/destination.svg';
import { EnRouteMapProps } from './types';
import { useGetOrderMissionQuery } from '@src/features/order/redux';
import { useFlightProvider } from '@src/features/order/provider';
import { useCameraLayout } from '@src/features/order/components/camera-view/camera-layout-context';
import FlightDrone from '@src/features/drone/components/flight-drone';

const EnRouteMap: React.FC<EnRouteMapProps> = (props) => {
  const { goTo, viewer, points = [], position, removePosition, resetGoHere, missionId: propMissionId, rotation } = props;
  const { missionId: urlMissionId } = useParams();
  const missionId = propMissionId ?? urlMissionId;

  const { data, isLoading } = useGetOrderMissionQuery(missionId, {
    skip: !missionId,
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
  });

  const { flightData } = useFlightProvider();
  const { confirm } = useConfirm();
  const { setCameraSource } = useCameraLayout();

  const [droneLocation, setDroneLocation] = React.useState<google.maps.LatLngLiteral | null>(null);

  const serialNumber = data?.drone?.serialNumber;

  React.useEffect(() => {
    if (serialNumber) {
      setCameraSource({
        serialNumber,
        isViewer: viewer,
        resetGoHere,
        isEnRoute: true,
      });
    } else {
      setCameraSource(null);
    }
  }, [serialNumber, viewer, resetGoHere]);

  // Reset drone location when missionId or drone changes
  React.useEffect(() => {
    setDroneLocation(null);
  }, [missionId, data?.drone?.serialNumber]);

  React.useEffect(() => {
    if (!flightData || !data?.drone) return;

    if (flightData.serialNumber !== data.drone.serialNumber) {
      return;
    }

    const lat = Number(flightData.latitude);
    const lng = Number(flightData.longitude);

    if (!isNaN(lat) && !isNaN(lng)) {
      setDroneLocation(prev => {
        if (prev?.lat === lat && prev?.lng === lng) return prev;
        return { lat, lng };
      });
    }
  }, [flightData, data?.drone?.serialNumber]);

  if (isLoading || !missionId || !data) {
    return null;
  }



  const path = data?.waypoints?.map((wp) => ({ lat: Number(wp.latitude), lng: Number(wp.longitude) }));

  const [startPoint] = data?.waypoints ?? [];
  const startLat = Number(startPoint?.latitude);
  const startLng = Number(startPoint?.longitude);

  const goHere = async (position: google.maps.LatLngLiteral) => {
    const confirmed = await confirm(
      'The drone will fly to the selected location.',
      'Confirm Go Here?'
    );
    if (!confirmed) return;
    goTo?.(position);
    removePosition?.();
  };

  return (
    <React.Fragment>

      <ArcPolyline.future path={path} />

      <FlightDrone
        drone={data?.drone ?    {
          ...data?.drone,
          latitude: droneLocation?.lat ?? Number(data?.drone?.latitude),
          longitude: droneLocation?.lng ?? Number(data?.drone?.longitude),
        } : undefined}
        rotation={rotation}
      />

      {data?.orders?.map(order => {
        const lat = Number(order.deliveryLocation.latitude);
        const lng = Number(order.deliveryLocation.longitude);
        if (isNaN(lat) || isNaN(lng)) return null;

        return (
          <Marker
            key={order.id}
            position={{ lat, lng }}
            icon={{
              url: destionation,
              scaledSize: new window.google.maps.Size(100, 50),
              origin: new window.google.maps.Point(0, 0),
              anchor: new window.google.maps.Point(17, 44),
            }}
          />
        );
      })}

      <Marker
        position={{ lat: startLat, lng: startLng }}
        icon={{
          url: takeoff,
          scaledSize: new window.google.maps.Size(80, 40),
          origin: new window.google.maps.Point(0, 0),
          anchor: new window.google.maps.Point(10, 20),
        }}
      />

      {points.map(point => (
        <Marker
          key={point.id}
          position={point.position}
          icon={{
            url: gohere,
            scaledSize: new window.google.maps.Size(80, 40),
            origin: new window.google.maps.Point(0, 0),
            anchor: new window.google.maps.Point(10, 20),
          }}
        />
      ))}

      {position && (
        <InfoWindow position={position} onCloseClick={removePosition} options={{ minWidth: 250 }}>
          <ArcStack spacing={2}>
            <ArcStack spacing={2} direction="row">
              <ArcCellInfo label="LATITUDE">
                <ArcText.h5 color="text.night">{position.lat.toFixed(5)}</ArcText.h5>
              </ArcCellInfo>
              <ArcCellInfo label="LONGITUDE">
                <ArcText.h5 color="text.night">{position.lng.toFixed(5)}</ArcText.h5>
              </ArcCellInfo>
            </ArcStack>

            <ArcButton.brand onClick={() => goHere(position)}>
              Go Here
            </ArcButton.brand>
          </ArcStack>
        </InfoWindow>
      )}

      <ArcPolyline.error
        path={[
          {
            lat: droneLocation?.lat ?? Number(data?.drone?.latitude),
            lng: droneLocation?.lng ?? Number(data?.drone?.longitude),
          },
          ...points.map(p => p.position)
        ]}
      />

    </React.Fragment>
  );
};

export default EnRouteMap;
